// Log all available environment variables
const envVars = { ...import.meta.env };
console.log('All environment variables:', 
  Object.keys(envVars).reduce((acc, key) => {
    acc[key] = key.includes('VITE_') ? envVars[key] : '[FILTERED]';
    return acc;
  }, {} as Record<string, string>)
);

// Log environment information
console.log('Environment Mode:', import.meta.env.MODE);
console.log('Development Mode:', import.meta.env.DEV);
console.log('Production Mode:', import.meta.env.PROD);

// Get and validate API URL
const apiUrl = (() => {
    // Try to get API URL from different sources
    const sources = {
        env: import.meta.env.VITE_API_URL,
        global: (window as any).__API_URL__,
        fallback: 'http://localhost:8000'
    };

    console.log('API URL Sources:', {
        env: sources.env,
        global: sources.global,
        fallback: sources.fallback
    });

    // Use the first available source
    const envUrl = sources.env || sources.global || sources.fallback;
    console.log('Selected API URL:', envUrl);

    if (!envUrl) {
        if (import.meta.env.PROD) {
            throw new Error('API URL is required in production mode');
        }
        console.warn(`No API URL found, using fallback: ${sources.fallback}`);
        return sources.fallback;
    }

    try {
        const url = new URL(envUrl);
        
        // Validate URL format
        if (!url.protocol || !url.host) {
            throw new Error('Invalid URL format: Missing protocol or host');
        }

        // Check for placeholder domains in production
        if (import.meta.env.PROD) {
            const placeholderDomains = [
                'your-api-domain.com',
                'example.com'
            ];
            
            const allowedDomains = [
                'railway.app',
                'localhost'
            ];

            if (placeholderDomains.some(domain => url.host.includes(domain))) {
                throw new Error(`Invalid API URL: Using placeholder domain (${url.host})`);
            }

            // In production, ensure we're using an allowed domain
            if (!allowedDomains.some(domain => url.host.includes(domain))) {
                console.warn(`Warning: Using non-standard domain in production: ${url.host}`);
            }
        }

        const finalUrl = url.toString();
        console.log('Validated API URL:', finalUrl);
        return finalUrl;
    } catch (e) {
        console.error('API URL Validation Error:', e);
        if (import.meta.env.PROD) {
            throw e;
        }
        console.warn(`Invalid API URL format, using fallback: ${sources.fallback}`);
        return sources.fallback;
    }
})();

// Log the final configuration
console.log('Final Configuration:', {
    apiUrl,
    mode: import.meta.env.MODE,
    isDev: import.meta.env.DEV,
    isProd: import.meta.env.PROD
});

// Default fetch options for all API requests
const fetchOptions = {
    credentials: 'omit' as RequestCredentials,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    mode: 'cors' as RequestMode,
};

// Helper function to construct full URLs for requests
const getFullUrl = (endpoint: string): string => {
    // Remove any leading or trailing slashes from the endpoint
    const cleanEndpoint = endpoint.replace(/^\/+|\/+$/g, '');
    // Remove any trailing slashes from the API URL
    const cleanApiUrl = apiUrl.replace(/\/+$/, '');
    // Combine with a single slash
    const fullUrl = `${cleanApiUrl}/${cleanEndpoint}`;
    console.log('Making request to:', fullUrl);
    return fullUrl;
};

// Helper function to handle API errors
const handleApiError = async (response: Response) => {
    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const error = new Error(
            errorData?.detail || 
            errorData?.message || 
            `API request failed with status ${response.status}`
        );
        (error as any).status = response.status;
        (error as any).data = errorData;
        throw error;
    }
    return response;
};

// Export configuration
export default {
    apiUrl,
    fetchOptions,
    getFullUrl,
    handleApiError,
}; 